import { Component, OnChanges, OnInit, Renderer2 } from '@angular/core';
import * as debug from 'debug';
import { SocketService } from '@app/core/socket.service';
import { BalanceHistoryService } from '@app/core/account/balance-history.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ReplyAbstractComponent } from '@app/shared/components/reply/reply-abstract.component';
const log = debug('edg:reply-crypto-gems');
const MersenneTwister = require('mersennetwister');

interface Award {
  result: number;
  payout: any[];
}

interface RandomNumbers {
  0: number[];
  1: number[];
  2: number[];
  3: number[];
  4: number[];
}

const scatterSymbol: number = 11;
const wildSymbol: number = 12;

@Component({
  selector: 'app-reply-crypto-gems',
  templateUrl: './reply-crypto-gems.component.html',
  styleUrls: ['./reply-crypto-gems.component.scss'],
})
export class ReplyCryptoGemsComponent extends ReplyAbstractComponent implements OnChanges {
  data: any = null;

  coins: string[] = [];
  betLines: number[] = [];
  combinations: number[][] = [];
  randomNumbers: RandomNumbers = { 0: [], 1: [], 2: [], 3: [], 4: [] };

  public readonly paytable: any[] = [
    { nine: { 2: 2, 3: 5, 4: 25, 5: 100 }, count: 0 }, // 0 9
    { ten: { 3: 5, 4: 25, 5: 100 }, count: 0 }, // 1 10
    { jocker: { 3: 5, 4: 25, 5: 100 }, count: 0 }, // 2 Jocker
    { queen: { 3: 5, 4: 25, 5: 100 }, count: 0 }, // 3 Queen
    { king: { 3: 5, 4: 25, 5: 100 }, count: 0 }, // 4 King
    { ace: { 3: 10, 4: 50, 5: 125 }, count: 0 }, // 5 Ace
    { eye: { 3: 10, 4: 50, 5: 250 }, count: 0 }, // 6 Eye
    { crookAndFlail: { 3: 10, 4: 75, 5: 250 }, count: 0 }, // 7 Crook and flail
    { gold: { 3: 15, 4: 100, 5: 400 }, count: 0 }, // 8 Gold
    { flower: { 2: 2, 3: 25, 4: 100, 5: 750 }, count: 0 }, // 9 Flower
    { scarab: { 2: 2, 3: 25, 4: 100, 5: 750 }, count: 0 }, // 10 Scarab
    { cleopatra: { 2: 10, 3: 200, 4: 2000, 5: 10000 }, count: 0 }, // 11 Cleopatra ( Wild)
    { sphinx: { 2: 2, 3: 5, 4: 20, 5: 100 }, count: 0 }, // 12 Sphinx (Scatter) // Multiplier
  ];

  payout: any;

  constructor(
    protected socket: SocketService,
    private sanitizer: DomSanitizer,
    private renderer: Renderer2,
    protected balanceHistory: BalanceHistoryService
  ) {
    super(socket, balanceHistory);

    this.coins = [
      'nine',
      'ten',
      'jocker',
      'queen',
      'king',
      'ace',
      'eye',
      'crookAndFlail',
      'gold',
      'flower',
      'scarab',
      'cleopatra',
      'sphinx',
    ];

    this.betLines = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

    this.combinations = [
      [5, 6, 7, 8, 9],
      [0, 1, 2, 3, 4],
      [10, 11, 12, 13, 14],
      [0, 6, 12, 8, 4],
      [10, 6, 2, 8, 14],
      [0, 1, 7, 13, 14],
      [10, 11, 7, 3, 4],
      [5, 1, 7, 13, 9],
      [5, 11, 7, 3, 9],
      [0, 6, 7, 8, 14],
      [10, 6, 7, 8, 4],
      [5, 1, 2, 8, 14],
      [5, 11, 12, 8, 4],
      [5, 6, 2, 8, 14],
      [5, 6, 12, 8, 4],
      [0, 1, 7, 13, 9],
      [10, 11, 7, 3, 9],
      [5, 1, 7, 13, 14],
      [5, 11, 7, 3, 4],
      [0, 1, 2, 8, 14],
    ];
  }

  // specified game
  protected initialize(data: any) {
    if (data) {
      if (data.game !== 'crypto-gems') {
        this._error = true;
        return;
      }
      this.data = data.gameData;
      this._loading = false;
    }

    const luckyNumbers: number[] = this.getLuckyReels(this.playerSeed, this.serverSeed);
    this.randomNumbers = this.joinLuckyNumbers(luckyNumbers);
    const award: Award = this.checkForAwards(luckyNumbers);
    this.payout = award.payout;
  }

  // not specified game
  protected generateGame() {
    const luckyNumbers: number[] = this.getLuckyReels(this.playerSeed, this.serverSeed);
    this.randomNumbers = this.joinLuckyNumbers(luckyNumbers);
    const award: Award = this.checkForAwards(luckyNumbers);
    this.payout = award.payout;
    this._loading = false;
  }

  getLuckyReels(clientSeed: string, serverSeed: string): number[] {
    const reels: number[][] = [
      [6, 3, 1, 8, 2, 5, 7, 1, 3, 9, 0, 1, 12, 3, 2, 11, 3, 5, 9, 4, 10, 3, 6, 4, 7, 1, 10, 0, 8, 1],
      [3, 2, 8, 3, 5, 11, 3, 2, 6, 0, 3, 8, 4, 10, 2, 9, 1, 10, 0, 12, 5, 7, 1, 4, 9, 2, 3, 6, 2, 7],
      [4, 0, 6, 1, 8, 4, 2, 11, 3, 1, 8, 0, 2, 10, 5, 1, 7, 5, 4, 10, 0, 1, 9, 3, 0, 7, 4, 6, 0, 12],
      [4, 0, 6, 5, 9, 2, 10, 1, 7, 2, 8, 3, 6, 2, 1, 7, 5, 3, 12, 1, 4, 8, 2, 7, 0, 5, 9, 0, 5, 11],
      [
        8,
        1,
        2,
        9,
        4,
        1,
        11,
        4,
        0,
        10,
        1,
        6,
        5,
        2,
        12,
        0,
        5,
        7,
        3,
        5,
        7,
        1,
        6,
        3,
        8,
        0,
        11,
        2,
        9,
        3,
        4,
        6,
        2,
        1,
        8,
        3,
        7,
        0,
        10,
        5,
        0,
      ],
    ];

    const result: number[] = [];

    clientSeed = clientSeed.replace('0x', '');
    serverSeed = serverSeed.replace('0x', '');
    const clSeedSplitted = clientSeed.match(/.{1,6}/g);
    const srvSeedSplitted = serverSeed.match(/.{1,6}/g);
    const seedsCombined = clSeedSplitted.concat(srvSeedSplitted);
    for (let i = 0; i < seedsCombined.length; i++) {
      seedsCombined[i.toString()] = parseInt(seedsCombined[i], 16);
    }

    const mt = new MersenneTwister();
    mt.seedArray(seedsCombined);

    for (let i = 0; i < 15; ++i) {
      result.push(0);
    }

    for (let i = 0; i < 5; ++i) {
      const randomIndex: number = mt.int() % reels[i].length;

      if (randomIndex + 2 > reels[i].length - 1) {
        if (randomIndex + 2 - reels[i].length === 1) {
          result[i] = reels[i][randomIndex];
          result[5 + i] = reels[i][0];
          result[10 + i] = reels[i][1];
        } else {
          result[i] = reels[i][randomIndex];
          result[5 + i] = reels[i][randomIndex + 1];
          result[10 + i] = reels[i][0];
        }
      } else {
        result[i] = reels[i][randomIndex];
        result[5 + i] = reels[i][randomIndex + 1];
        result[10 + i] = reels[i][randomIndex + 2];
      }
    }

    return result;
  }

  private joinLuckyNumbers(luckyNumbers: number[]): RandomNumbers {
    const randomNumbers: RandomNumbers = {
      0: [],
      1: [],
      2: [],
      3: [],
      4: [],
    };
    for (let i = 0; i < 5; ++i) {
      randomNumbers[i].push.apply(randomNumbers[i], [luckyNumbers[i], luckyNumbers[5 + i], luckyNumbers[10 + i]]);
    }

    return randomNumbers;
  }

  private checkForAwards(luckyNumbers: number[]): Award {
    let award: Award = { result: 0, payout: [] };

    const fields: number[] = luckyNumbers;

    // Normal cases
    for (let i = 0; i < this.combinations.length; ++i) {
      const gameResult: object | null = this.checkGameCase(i, fields);
      award = this.calcAward(award, gameResult);
    }

    // Scatter case
    const bonusResult: object | null = this.checkForBonus(fields);
    award = this.calcAward(award, bonusResult);

    return award;
  }

  private calcAward(award: Award, gameResult: any): Award {
    if (gameResult && gameResult.payout) {
      if (gameResult.payout.quantity >= 3) {
        award.payout.push(gameResult.payout);
      }
      if (
        gameResult.payout.quantity === 2 &&
        (gameResult.payout.symbol === 3 ||
          gameResult.payout.symbol === 7 ||
          gameResult.payout.symbol === 8 ||
          gameResult.payout.symbol === 10)
      ) {
        award.payout.push(gameResult.payout);
      }
    }

    return award;
  }

  private checkForBonus(fields: number[]): null | object {
    let count: number = 0;
    let result: null | object = null;
    const path: number[] = [];

    fields.forEach((symbol, index) => {
      if (symbol === scatterSymbol) {
        ++count;
        path.push(index);
      }
    });

    if (count >= 3) {
      result = {
        payout: {
          sum: this.paytable[scatterSymbol][Object.keys(this.paytable[scatterSymbol])[0]][count],
          quantity: count,
          symbol: scatterSymbol,
          path: path,
        },
      };
    }

    return result;
  }

  private checkGameCase(game: number, fields: number[]): object {
    const path: number[] = this.combinations[game];

    const max: any = this.findMaxField(fields, path);

    const result: null | object = {
      payout: {
        sum: this.paytable[max.symbol][Object.keys(this.paytable[max.symbol])[0]][max.count],
        quantity: max.count,
        symbol: max.symbol,
        path: path,
      },
    };

    return result;
  }

  private findMaxField(fields: number[], indexArr: number[]): object {
    /*
            0  1  2  3  4
            5  6  7  8  9
            10 11 12 13 14
            */

    // Ensure order of reels

    const reels: number[] = [0, 0, 0, 0, 0];

    indexArr.forEach(item => {
      if (item === 0 || item === 5 || item === 10) {
        reels[0] = item;
      }
      if (item === 1 || item === 6 || item === 11) {
        reels[1] = item;
      }
      if (item === 2 || item === 7 || item === 12) {
        reels[2] = item;
      }
      if (item === 3 || item === 8 || item === 13) {
        reels[3] = item;
      }
      if (item === 4 || item === 9 || item === 14) {
        reels[4] = item;
      }
    });

    // Convert index to real symbols
    reels.forEach((symbol, index) => {
      reels[index] = fields[symbol];
    });

    let wildsCount: number = 0;
    for (let i = 0; i < reels.length; ++i) {
      if (reels[i] === wildSymbol) {
        ++wildsCount;
      } else {
        break;
      }
    }

    let maxSymbol: number | string = reels[0];
    let maxCount: number = 1;

    for (let i = 1; i < reels.length; i++) {
      if (maxSymbol === scatterSymbol || reels[i] === scatterSymbol) {
        break;
      }

      if (reels[i] === maxSymbol || reels[i] === wildSymbol || maxSymbol === wildSymbol) {
        if (reels[i] !== wildSymbol) {
          maxSymbol = reels[i];
        }
        ++maxCount;
      } else {
        break;
      }
    }

    // Check whether wild line is higher
    const maxSymbolAward: number = this.paytable[maxSymbol][Object.keys(this.paytable[maxSymbol])[0]][maxCount];
    const wildSymbolAward: number = this.paytable[wildSymbol][Object.keys(this.paytable[wildSymbol])[0]][wildsCount];

    if (maxSymbolAward <= wildSymbolAward) {
      maxSymbol = wildSymbol;
      maxCount = wildsCount;
    }

    return { symbol: maxSymbol, count: maxCount };
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/shared/components/reply/reply-crypto-gems/reply-crypto-gems.component.ts